// @flow
/**
 * Undo and redo for the page draft. Wraps the draft reducer: every edit keeps
 * the previous draft (up to HISTORY_LIMIT), undo/redo move between them, and
 * a burst of typing in the same settings field counts as one step.
 *
 * Refetching the same page (after a save) keeps the history, so a saved change
 * can still be undone; loading another page or an old version starts fresh.
 */

export const HISTORY_LIMIT = 50;
const COALESCE_MS = 1000;
const EDIT_ACTIONS = ['update_slot', 'change_version', 'change_template_settings', 'change_page_settings', 'change_category'];

// `step` counts undo steps taken (edits, undos, redos), so a prompt such as
// "Removed X · Undo" can tell whether its step is still the latest.
type History = { past: Array<Object>, future: Array<Object>, lastEdit: ?{ key: string, at: number }, step: number };

// Typing in one field (a page setting, the template settings, a section's
// field in the inspector) is one step.
const coalesceKey = (action) => {
  if (action.type === 'change_page_settings') return `page:${action.payload.key}`;
  if (action.type === 'change_template_settings') return 'template';
  if (action.type === 'update_slot' && action.coalesce) return `slot:${action.coalesce}`;
  return null;
};

// An earlier draft keeps the latest server timestamp: the save conflict check
// compares it, and an old one would make the next save look outdated.
const withServerTime = (draft: Object, current: Object) => (
  draft && current && draft.updatedAt !== current.updatedAt ? { ...draft, updatedAt: current.updatedAt } : draft
);

export const withHistory = (reducer: (Object, Object) => Object) => (state: Object, action: Object) => {
  const past = state.past || [];
  const future = state.future || [];

  if (action.type === 'undo') {
    if (!past.length) return state;
    return {
      ...state,
      draft: withServerTime(past[past.length - 1], state.draft),
      past: past.slice(0, -1),
      future: [state.draft, ...future],
      lastEdit: null,
      step: (state.step || 0) + 1
    };
  }
  if (action.type === 'redo') {
    if (!future.length) return state;
    return {
      ...state,
      draft: withServerTime(future[0], state.draft),
      past: [...past, state.draft],
      future: future.slice(1),
      lastEdit: null,
      step: (state.step || 0) + 1
    };
  }

  // Before the server replaces the draft (restoring a version): keep the
  // current draft as an undo step, so Undo brings it back.
  if (action.type === 'checkpoint') {
    return {
      ...state,
      past: [...past, state.draft].slice(-HISTORY_LIMIT),
      future: [],
      lastEdit: null,
      step: (state.step || 0) + 1,
      checkpoint: state.draft
    };
  }
  // The replacement didn't happen: drop that step again.
  if (action.type === 'cancel_checkpoint') {
    if (!state.checkpoint || state.checkpoint !== state.draft || past[past.length - 1] !== state.draft) return state;
    return { ...state, past: past.slice(0, -1), step: (state.step || 0) + 1, checkpoint: null };
  }

  const next = reducer(state, action);

  if (EDIT_ACTIONS.includes(action.type)) {
    if (next.draft === state.draft) return next;
    const key = coalesceKey(action);
    const now = typeof action.now === 'number' ? action.now : Date.now();
    const coalesce = key && state.lastEdit && state.lastEdit.key === key && now - state.lastEdit.at < COALESCE_MS;
    const history: History = {
      past: coalesce ? past : [...past, state.draft].slice(-HISTORY_LIMIT),
      future: [],
      lastEdit: key ? { key, at: now } : null,
      step: (state.step || 0) + 1
    };
    return { ...next, ...history };
  }

  // `adopted` is the draft as last taken from the server; anything else is a local edit.
  if (action.type === 'replace_draft') {
    const samePage = state.draft && action.payload && state.draft._id === action.payload._id;
    return samePage
      ? { ...next, past, future, adopted: next.draft }
      : { ...next, past: [], future: [], lastEdit: null, adopted: next.draft };
  }

  if (action.type === 'replace_version') {
    return { ...next, past: [], future: [], lastEdit: null, adopted: null };
  }

  if (action.type === 'patch_draft_meta') {
    // A timestamp change isn't an edit: if the draft matched the server, it still does.
    return { ...next, past, future, adopted: state.adopted === state.draft ? next.draft : state.adopted };
  }

  return { ...next, past, future };
};

/** True when the draft has edits that weren't taken from the server. */
export const hasLocalEdits = (state: Object) => Boolean(state && state.draft && state.adopted && state.draft !== state.adopted);

export const historyStep = (state: Object) => (state && state.step) || 0;

export const canUndo = (state: Object) => Boolean(state && state.past && state.past.length);
export const canRedo = (state: Object) => Boolean(state && state.future && state.future.length);
